import { useState, useEffect } from "react";
import Lottie from "react-lottie";
import { ClubDayBadgesDetailData } from "../../Api";
import confettiAnimation from "./confetti-port.json";
import { useAuthContext } from "../../context/auth";

const badgeTypeLabels: Record<string, string> = {
  FIRST_PRESENT: "첫 출석",
  WEEK_3: "주 3회 출석",
  WEEK_5: "주 5회 출석",
  MONTH_20: "월 20회 출석",
  "50": "50회",
  "100": "100회",
  "200": "200회",
  "300": "300회",
  "500": "500회",
};

const badgeTypeImages: Record<string, string> = {
  FIRST_PRESENT: "badge_active1",
  WEEK_3: "badge_active2",
  WEEK_5: "badge_active3",
  MONTH_20: "badge_active4",
  "50": "badge_active5",
  "100": "badge_active6",
  "200": "badge_active7",
  "300": "badge_active8",
  "500": "badge_active9",
};

function ClubDayPortrait({
  data,
}: {
  data: ClubDayBadgesDetailData["classAchievements"][];
}) {
  const { index } = useAuthContext();
  const [flippedBadges, setFlippedBadges] = useState<boolean[]>([]);
  const [showInfo, setShowInfo] = useState<boolean[]>([]);
  const [showConfetti, setShowConfetti] = useState(false);

  const currentData = data[index ?? 0] || [];

  useEffect(() => {
    // Reset states when currentIndex changes
    setFlippedBadges(new Array(currentData.length).fill(false));
    setShowInfo(new Array(currentData.length).fill(false));
    setShowConfetti(false);

    // Start flipping badges after 1 second, one by one with 300ms delay
    const timer = setTimeout(() => {
      currentData.forEach((_, badgeIndex) => {
        setTimeout(() => {
          // Start flip animation
          setFlippedBadges((prev) => {
            const newFlipped = [...prev];
            newFlipped[badgeIndex] = true;
            return newFlipped;
          });

          // Show info and trigger confetti after flip animation (400ms)
          setTimeout(() => {
            setShowInfo((prev) => {
              const newShowInfo = [...prev];
              newShowInfo[badgeIndex] = true;
              return newShowInfo;
            });

            // Show confetti on first badge flip
            if (badgeIndex === 0) {
              setShowConfetti(true);
            }
          }, 400);
        }, badgeIndex * 300);
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [index, data, currentData]);

  const lottieOptions = {
    loop: true,
    autoplay: true,
    animationData: confettiAnimation,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  return (
    <div className="h-screen w-screen bg-[#000]">
      {/* Confetti Background */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-10">
          <Lottie
            options={lottieOptions}
            height="100%"
            width="100%"
            isStopped={false}
            isPaused={false}
          />
        </div>
      )}

      <div className="h-full w-full flex flex-col gap-[100px] relative py-[256px] px-[50px] items-center z-20">
        <div className="relative">
          <h1 className="text-[100px] font-extrabold text-white">CLUB DAY</h1>
          {/* {data.length > 1 && (
            <p className="text-[40px] font-medium absolute -right-[60px] top-1/2 -translate-y-1/2 text-white">
              {(index ?? 0) + 1}/{data.length}
            </p>
          )} */}
        </div>

        <div
          className="flex flex-wrap justify-center h-full items-center"
          style={{ gap: "80px 20px" }}
        >
          {currentData.map((item, badgeIndex) => (
            <div
              key={item.id}
              className="flex flex-col items-center gap-5 h-[556px]"
            >
              <div className="relative w-[360px] h-[360px] preserve-3d">
                <div
                  className={`card-container ${
                    flippedBadges[badgeIndex] ? "flipped" : ""
                  }`}
                >
                  {/* Back of card */}
                  <div className="card-face card-back">
                    <img
                      src="/disabled_bedge.svg"
                      className="w-full h-full"
                      alt="disabled badge"
                    />
                  </div>
                  {/* Front of card */}
                  <div className="card-face card-front">
                    <img
                      src={`/badge/${
                        badgeTypeImages[item.type] || "badge_active1"
                      }.svg`}
                      className="w-full h-full"
                      alt="active badge"
                    />
                  </div>
                </div>
              </div>

              {showInfo[badgeIndex] && (
                <div className="flex flex-col items-center gap-4 animate-fadeIn">
                  <h2 className="text-[80px] font-bold text-white text-center leading-tight">
                    {badgeTypeLabels[item.type] || item.type}
                  </h2>

                  <div className="flex items-center gap-4">
                    <div className="w-[60px] h-[60px] rounded-full border-2 border-white overflow-hidden flex-shrink-0">
                      <img
                        src={item.userImage || "/defaultProfileImage.png"}
                        alt={item.userName || "User"}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-[56px] font-medium text-[#8D9CB6] truncate">
                      {item.userName || "Unknown User"}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .preserve-3d {
          transform-style: preserve-3d;
          perspective: 1000px;
        }
        
        .card-container {
          width: 100%;
          height: 100%;
          position: relative;
          transition: transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          transform-style: preserve-3d;
        }
        
        .card-container.flipped {
          transform: rotateY(180deg);
        }
        
        .card-face {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
        }
        
        .card-back {
          transform: rotateY(0deg);
        }
        
        .card-front {
          transform: rotateY(180deg);
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-in-out;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default ClubDayPortrait;
